import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { useLanguage } from '../../context/LanguageContext';
import { LAYOUTS } from './PagesManager';
import './PagesManager.css';
import './PhotoBookPreview.css';

// Reference width the raw px text font-size values were designed against —
// .page-editor's un-breakpointed max-width in PagesManager.css. Font sizes
// scale relative to this so a page rendered wider/narrower here (the flip
// book's own responsive width) keeps the same visual proportions as the
// actual editor instead of shrinking/ballooning the text.
const PAGE_TEXT_REFERENCE_WIDTH = 320;

// Read-only render of one interior page — same slot/text layout data as
// PageEditor, minus every drag/click/edit handler.
const StaticPage = React.forwardRef(({ page, textScale }, ref) => {
  const layout = LAYOUTS.find(l => l.id === page.layout) || LAYOUTS[0];
  return (
    <div className="pf-page" ref={ref}>
      <div className="page-content" style={{ backgroundColor: page.backgroundColor || '#ffffff' }}>
        {layout.slots.map((slot, slotIndex) => {
          const effectiveSlot = page.slotSizes?.[slotIndex] || slot;
          const image = page.images?.[slotIndex];
          return (
            <div
              key={slotIndex}
              className={`photo-slot${image ? ' has-image' : ''}`}
              style={{
                left: `${effectiveSlot.x}%`,
                top: `${effectiveSlot.y}%`,
                width: `${effectiveSlot.width}%`,
                height: `${effectiveSlot.height}%`,
              }}
            >
              {image && (
                <div className="slot-image-wrapper">
                  <img
                    src={image.src}
                    alt=""
                    draggable={false}
                    className="slot-image-element"
                    style={{
                      transform: `translate(${image.panX || 0}px, ${image.panY || 0}px) rotate(${image.rotation || 0}deg) scale(${image.zoom || 1})`
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
        {(page.texts || []).map(textEl => (
          <div
            key={textEl.id}
            className="text-element"
            style={{ left: `${textEl.x}%`, top: `${textEl.y}%`, width: `${textEl.width || 60}%` }}
          >
            <span style={{
              display: 'block',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.3,
              fontFamily: textEl.fontFamily || 'Playfair Display',
              fontSize: `${(textEl.fontSize || 18) * textScale}px`,
              color: textEl.color || '#000000',
              fontWeight: textEl.bold ? 'bold' : 'normal',
              fontStyle: textEl.italic ? 'italic' : 'normal',
              textDecoration: textEl.underline ? 'underline' : 'none',
              textAlign: textEl.align || 'center',
            }}>
              {textEl.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});

// Full-screen "read this like a real book" preview — a page-turn flip book
// (react-pageflip) built from the same interior-page data as the editor,
// shown right before the customer commits to the order form. Deliberately
// interior pages only, no front/back cover face — the cover has its own
// dedicated editor view already and isn't part of this "flip through it"
// read.
const PhotoBookPreview = ({ pages, onClose, onProceedToOrder }) => {
  const { t } = useLanguage();
  const flipRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [pageWidth, setPageWidth] = useState(320);
  useEffect(() => {
    const calc = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const widthBudget = (vw - 180) / 2;
      const heightBudget = ((vh - 200) * 210) / 297;
      const w = Math.max(160, Math.min(widthBudget, heightBudget, 480));
      setPageWidth(Math.round(w));
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);
  const pageHeight = Math.round((pageWidth * 297) / 210);
  const textScale = pageWidth / PAGE_TEXT_REFERENCE_WIDTH;

  const children = useMemo(
    () => pages.map(p => <StaticPage key={p.id} page={p} textScale={textScale} />),
    [pages, textScale]
  );

  const totalCount = children.length;

  const goPrev = useCallback(() => flipRef.current?.pageFlip()?.flipPrev(), []);
  const goNext = useCallback(() => flipRef.current?.pageFlip()?.flipNext(), []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, goPrev, goNext]);

  return (
    <div className="pf-overlay" onClick={onClose}>
      <div className="pf-topbar" onClick={e => e.stopPropagation()}>
        <span className="pf-title">{t('preview.title')}</span>
        <button type="button" className="pf-close-btn" onClick={onClose}>
          ✕ {t('preview.editBtn')}
        </button>
      </div>

      <div className="pf-stage" onClick={e => e.stopPropagation()}>
        <button
          type="button"
          className="pf-arrow pf-arrow-left"
          onClick={goPrev}
          disabled={currentIndex <= 0}
          aria-label={t('preview.prev')}
        >‹</button>

        <HTMLFlipBook
          key={`${pageWidth}-${totalCount}`}
          ref={flipRef}
          width={pageWidth}
          height={pageHeight}
          size="fixed"
          showCover={false}
          usePortrait={true}
          drawShadow={true}
          flippingTime={650}
          maxShadowOpacity={0.5}
          mobileScrollSupport={true}
          className="pf-flipbook"
          onFlip={e => setCurrentIndex(e.data)}
        >
          {children}
        </HTMLFlipBook>

        <button
          type="button"
          className="pf-arrow pf-arrow-right"
          onClick={goNext}
          disabled={currentIndex >= totalCount - 1}
          aria-label={t('preview.next')}
        >›</button>
      </div>

      <div className="pf-bottombar" onClick={e => e.stopPropagation()}>
        <span className="pf-counter">{t('preview.pageCounter', { current: currentIndex + 1, total: totalCount })}</span>
        {onProceedToOrder && (
          <button type="button" className="pf-order-btn" onClick={onProceedToOrder}>
            {t('designer.continueOrder')}
          </button>
        )}
      </div>
    </div>
  );
};

export default PhotoBookPreview;
